import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint } from '@/helpers/api'

import { templatesQueryKeys } from '@/hooks/templates/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Template } from '@/types/library/Template'

interface UseTemplatesArgs<T> {
  orgId: UUID | ''
  selector?: (data: PaginatedResponse<Template>) => T
}

export const fetchTemplates = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Template>> => {
  if (!orgId) {
    throw new Error('No org ID provided.')
  }
  if (accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/templates`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<Template>
}

export const useTemplates = <T = Template[]>({
  orgId,
  selector,
}: UseTemplatesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const select = useCallback(
    (data: PaginatedResponse<Template>) => {
      if (selector) {
        return selector(data)
      }
      return data.data as unknown as T
    },
    [selector],
  )

  const query = useQuery({
    queryFn: () => fetchTemplates(orgId, accessToken, { limit: 10000 }), // TODO: remove this
    queryKey: templatesQueryKeys.organization({ orgId }),
    staleTime: Infinity,
    select,
  })
  return query
}

interface UseTemplateCountArgs {
  orgId: UUID | ''
}

interface TemplateCount {
  count: number
}

const selectTemplateCount = (
  data: PaginatedResponse<Template>,
): TemplateCount => {
  return { count: data.count }
}

export const useTemplateCount = ({ orgId }: UseTemplateCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: templatesQueryKeys.count({ orgId }),
    // TODO: remove this limit
    queryFn: () => fetchTemplates(orgId, accessToken, { limit: 10000 }),
    enabled: !!orgId && !!accessToken,
    select: selectTemplateCount,
  })
}
